import api from './api';

/**
 * Structured jurisdiction & address.
 * - state_code is ISO 3166-2:IN (e.g. "IN-MH"); validate against indianStates.ts
 * - pin_code must be exactly 6 digits and not start with 0
 * - country defaults to "IN" server-side; UI hides until NRI cases come online
 */
export interface ClientCreate {
  display_name: string;
  /**
   * Workspace tag (criminal vs civil sub-area). Optional — when omitted the
   * backend derives it from the X-Active-Role header. We send it explicitly
   * from the active workspace so a client created in Criminal is tagged
   * criminal (mirrors NewMatterPage's practice_area binding).
   */
  practice_area?: string;
  contact_phone?: string;
  contact_email?: string;
  occupation?: string;
  relationship_notes?: string;
  /** P2-11: how the client found the lawyer (referrer name / channel). */
  referral_source?: string;
  state_code?: string;
  district?: string;
  city?: string;
  locality?: string;
  pin_code?: string;
  country?: string;
}

export interface ClientUpdate {
  display_name?: string;
  /** Re-tag a client to a different workspace. */
  practice_area?: string;
  contact_phone?: string;
  contact_email?: string;
  occupation?: string;
  relationship_notes?: string;
  referral_source?: string;
  photo_url?: string;
  state_code?: string;
  district?: string;
  city?: string;
  locality?: string;
  pin_code?: string;
  country?: string;
}

export interface ClientResponse {
  id: string;
  owner_user_id: string;
  /** Workspace tag: which practice-area lens (criminal/civil sub-area) this client belongs to. */
  practice_area: string | null;
  display_name: string;
  normalized_name: string;
  contact_phone: string | null;
  contact_email: string | null;
  occupation: string | null;
  relationship_notes: string | null;
  referral_source: string | null;
  photo_url: string | null;
  state_code: string | null;
  district: string | null;
  city: string | null;
  locality: string | null;
  pin_code: string | null;
  country: string;
  is_archived: boolean;
  matter_count: number;
  next_hearing: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientListResponse {
  clients: ClientResponse[];
  total_count: number;
}

/** P2-1: shape returned by GET /clients/conflict-check */
export interface ConflictCheckHit {
  kind: 'client' | 'opposing_counsel' | 'case_title' | 'notes';
  matched_text: string;
  client_id: string;
  client_display_name: string;
  matter_id: number | null;
  matter_case_title: string | null;
  matter_case_number: string | null;
}

export interface ConflictCheckResponse {
  query: string;
  hits: ConflictCheckHit[];
}

export const clientsApi = {
  createClient: async (data: ClientCreate): Promise<ClientResponse> => {
    const response = await api.post('/clients/', data);
    return response.data;
  },

  listClients: async (includeArchived = false): Promise<ClientListResponse> => {
    const response = await api.get('/clients/', { params: { include_archived: includeArchived } });
    return response.data;
  },

  getClient: async (clientId: string): Promise<ClientResponse> => {
    const response = await api.get(`/clients/${clientId}`);
    return response.data;
  },

  updateClient: async (clientId: string, data: ClientUpdate): Promise<ClientResponse> => {
    const response = await api.patch(`/clients/${clientId}`, data);
    return response.data;
  },

  archiveClient: async (clientId: string): Promise<void> => {
    await api.delete(`/clients/${clientId}`, { params: { archive: true } });
  },

  deleteClient: async (clientId: string): Promise<void> => {
    await api.delete(`/clients/${clientId}`, { params: { archive: false } });
  },

  /**
   * P2-1: search the lawyer's existing workspace for parties / opposing
   * counsel / case titles that match `q`. Used by the New Client form
   * to surface conflict-of-interest signals before the lawyer creates a
   * potentially-conflicting client. Returns at most `limit` hits.
   */
  conflictCheck: async (
    q: string,
    limit = 10,
  ): Promise<ConflictCheckResponse> => {
    const response = await api.get('/clients/conflict-check', {
      params: { q, limit },
    });
    return response.data;
  },
};
